import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import langDocumentDownloadLogTable from '../../../document/langs/langDocumentDownloadLogTable';
import { GridRenderCellParams, GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { Box, Grid, TextField } from '@mui/material';
import CAppDataGrid from '../../../../app/components/CAppDataGrid';
import useDocumentTypes from '../../../document/hooks/useDocumentTypes';
import moment from 'moment';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import CAppFormKeyboardDatePicker from '../../../../app/components/Form/CAppFormKeyboardDatePicker';
import CAppFilterSelect from '../../../../app/components/CAppFilterSelect';
import IFund from '../../../investor/interfaces/fund/IFund';
import _ from 'lodash';
import IUserCompany from '../../../user/interfaces/IUserCompany';
import IDocumentType from '../../../document/interfaces/IDocumentType';
import HDocumentFile from '../../../document/helpers/HDocumentFile';
import AppFormatDate from '../../../../app/features/format/AppFormatDate';
import CAppFormTextField from '../../../../app/components/Form/CAppFormTextField';
import langAdminDocumentDownloadLogs from '../../langs/langAdminDocumentDownloadLogs';
import useDocumentDownloadLogs from '../../../document/hooks/useDocumentDownloadLogs';
import AppFormatBoolean from '../../../../app/features/format/AppFormatBoolean';
import IInvestorGroup from '../../../investor/interfaces/IInvestorGroup';

type DownloadLogsFilter = {
	keywords: string;
	startDate: Date;
	endDate: Date;
	fundIds: number[];
	groupIds: number[];
	downloadState?: string; // TODO define in future a real typed state, actually 'yes' | 'no';
};
const FDocumentDownloadLogs = () => {
	const [filters, setFilters] = useState<DownloadLogsFilter>({
		keywords: '',
		startDate: moment().subtract(1, 'month').toDate(),
		endDate: moment().add(1, 'day').toDate(),
		fundIds: [],
		groupIds: [],
	});

	const { data: logs } = useDocumentDownloadLogs(filters?.startDate, filters?.endDate);
	const { documentTypes } = useDocumentTypes();

	const downloadStates = [
		{
			tag: 'yes',
			name: 'Yes',
		},
		{
			tag: 'no',
			name: 'No',
		},
	] as const;

	const { t } = useTranslation();

	const [rows, setRows] = useState<GridRowsProp>();

	const [funds, setFunds] = useState<IFund[]>();
	const [groups, setGroups] = useState<IInvestorGroup[]>();

	useEffect(() => {
		if (logs) {
			setFunds(
				_.uniqBy(
					logs.map((x) => x.fund),
					'id',
				),
			);
			setGroups(
				_.uniqBy(
					logs.filter((x) => x.company && x.company.group).map((x) => x.company.group),
					'id',
				),
			);
		}
	}, [logs]);

	useEffect(() => {
		if (logs && documentTypes && filters) {
			let logsTmp = _.cloneDeep(logs);
			if (filters.fundIds.length) {
				logsTmp = logsTmp.filter((x) => filters.fundIds?.includes(x.fund.id));
			}
			if (filters.groupIds.length) {
				logsTmp = logsTmp.filter((x) => filters.groupIds.includes(x.company?.group?.id));
			}
			logsTmp = logsTmp.map((x) =>
				x.isImpersonated
					? {
							...x,
							accessTime: undefined,
					  }
					: x,
			);
			if (filters.downloadState === 'yes') {
				logsTmp = logsTmp.filter((x) => x.accessTime);
			} else if (filters.downloadState === 'no') {
				logsTmp = logsTmp.filter((x) => !x.accessTime);
			}

			if (filters.keywords) {
				const searchWords = filters.keywords.split(' ');
				searchWords.forEach((word) => {
					logsTmp = logsTmp.filter((x) =>
						HDocumentFile.cleanWord(x.document.name).includes(HDocumentFile.cleanWord(word)),
					);
				});
			}

			setRows(
				logsTmp.map((x, position) => {
					return {
						id: position,
						name: x.document.name,
						groupName: x.company?.group?.name,
						['fund.name']: x.fund?.name,
						accessTime: x.accessTime,
						releaseDate: x.document?.releaseDate,
					};
				}),
			);
		}
	}, [documentTypes, logs, t, filters]);

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: t(langDocumentDownloadLogTable.document_name.tag),
			minWidth: 300,
			flex: 1,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'fund.name',
			headerName: t(langDocumentDownloadLogTable.fund.tag),
			width: 200,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'groupName',
			headerName: t(langDocumentDownloadLogTable.group.tag),
			width: 250,
			align: 'left',
			headerAlign: 'left',
		},
		{
			field: 'releaseDate',
			headerName: t(langDocumentDownloadLogTable.release_date.tag),
			width: 150,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) => <AppFormatDate date={params.value} />,
		},
		{
			field: 'accessTime',
			headerName: t(langDocumentDownloadLogTable.access_time.tag),
			width: 150,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) => (
				<AppFormatBoolean
					value={params.value !== null ? true : false}
					textIfTrue={<AppFormatDate date={params.value} />}
					textIfFalse="-"
					hideIcon
				/>
			),
		},
	];

	return (
		<>
			{rows ? (
				<>
					<Box sx={{ marginBottom: '1rem', marginTop: '2rem' }}>
						<Grid container spacing={3}>
							<Grid item sm={2}>
								<CAppFormKeyboardDatePicker
									label={t(langAdminDocumentDownloadLogs.filter_start_released_date.tag)}
									value={filters?.startDate}
									inputFormat="DD/MM/YYYY"
									onChange={(obj) =>
										setFilters((prevData) => ({ ...prevData, startDate: obj as Date }))
									}
									renderInput={(params) => (
										<CAppFormTextField
											{...params}
											fullWidth
											variant="outlined"
											inputProps={{
												...params.inputProps,
												placeholder: 'JJ/MM/AAAA',
												style: { textAlign: 'center' },
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item sm={2}>
								<CAppFormKeyboardDatePicker
									label={t(langAdminDocumentDownloadLogs.filter_end_released_date.tag)}
									value={filters?.endDate}
									inputFormat="DD/MM/YYYY"
									onChange={(obj) =>
										setFilters((prevData) => ({ ...prevData, endDate: obj as Date }))
									}
									renderInput={(params) => (
										<CAppFormTextField
											{...params}
											fullWidth
											variant="outlined"
											inputProps={{
												...params.inputProps,
												placeholder: 'JJ/MM/AAAA',
												style: { textAlign: 'center' },
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item sm={2}>
								<TextField
									variant="outlined"
									fullWidth
									size="small"
									label={t(langAdminDocumentDownloadLogs.filter_keywords.tag)}
									value={filters?.keywords}
									onChange={(event) =>
										setFilters((prevData) => ({ ...prevData, keywords: event.target.value }))
									}
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: '45px',
										},
										'& .MuiInputBase-input': {
											paddingLeft: '10px',
										},
										marginBottom: '15px',
									}}
									InputProps={{
										startAdornment: <AppCustomIcon tag="search" />,
									}}
								/>
							</Grid>

							<Grid item sm={2}>
								<CAppFilterSelect
									multiple
									multipleRenderTags={(val: IFund[]) => (
										<Box style={{ paddingLeft: '5px', fontSize: '0.85rem' }}>
											{val ? val.length : 0} fund(s) selected
										</Box>
									)}
									value={
										funds
											? funds.filter(
													(x) => filters && filters.fundIds && filters.fundIds.includes(x.id),
											  )
											: null
									}
									onChange={(val: IFund[]) => {
										setFilters((prevData) => ({
											...prevData,
											fundIds: val && val.length > 0 ? val.map((x) => x.id) : [],
										}));
									}}
									label={t(langAdminDocumentDownloadLogs.filter_funds.tag)}
									size="small"
									options={
										funds
											? funds.map((x: IFund) => ({
													id: x.id,
													name: x.name,
											  }))
											: null
									}
								/>
							</Grid>

							<Grid item sm={2}>
								<CAppFilterSelect
									multiple
									multipleRenderTags={(val: IUserCompany[]) => (
										<Box style={{ paddingLeft: '5px', fontSize: '0.85rem' }}>
											{val ? val.length : 0} group(s) selected
										</Box>
									)}
									value={
										groups
											? groups.filter(
													(x) =>
														filters && filters.groupIds && filters.groupIds.includes(x.id),
											  )
											: null
									}
									onChange={(val: IInvestorGroup[]) => {
										setFilters((prevData) => ({
											...prevData,
											groupIds: val && val.length > 0 ? val.map((x) => x.id) : [],
										}));
									}}
									label={t(langAdminDocumentDownloadLogs.filter_groups.tag)}
									size="small"
									options={
										groups
											? groups.map((x: IInvestorGroup) => ({
													id: x.id,
													name: x.name,
											  }))
											: null
									}
								/>
							</Grid>
							<Grid item sm={2}>
								<CAppFilterSelect
									value={
										downloadStates
											? downloadStates.find((x) => x.tag === filters.downloadState)
											: null
									}
									onChange={(val: IDocumentType) => {
										setFilters((prevData) => ({
											...prevData,
											downloadState: val?.tag,
										}));
									}}
									label={t(langAdminDocumentDownloadLogs.filter_download_state.tag)}
									size="small"
									options={
										downloadStates
											? downloadStates.map((x: { tag: string; name: string }) => ({
													tag: x.tag,
													name: x.name,
											  }))
											: null
									}
								/>
							</Grid>
						</Grid>
					</Box>
					<CAppDataGrid
						className="document-files-desktop"
						getRowClassName={(params) => `row-status--${params.row.fund?.isRealized}`}
						rows={rows}
						columns={columns}
						autoHeight
						pagination
						disableSelectionOnClick
						disableColumnSelector
						disableColumnMenu
					/>
				</>
			) : (
				''
			)}
		</>
	);
};

export default FDocumentDownloadLogs;
